import axios from "axios";
import { getHiddenStems } from "../_helpers";


/**
 * BZChart - 前端八字基础类
 * 用于构造四柱八字信息，作为参数传递给各个页面
 */
class BZChart {
  constructor({
    user_id = null,
    mingzhu_name = "",
    mingzhu_gender = "",
    shudi = "",
    zhanti_type = "",
    zhanti = "",
    year = null,
    month = null,
    day = null,
    hour = null,
    year_pillar = "",
    month_pillar = "",
    day_pillar = "",
    hour_pillar = "",
    owner_feedback = "",
    master_record = "",
    created_at = null,
    chart_id = null,
  } = {}) {
    this.user_id = user_id;
    this.mingzhu_name = mingzhu_name;
    this.mingzhu_gender = mingzhu_gender;
    this.shudi = shudi;
    this.zhanti_type = zhanti_type;
    this.zhanti = zhanti;
    this.year = year;
    this.month = month;
    this.day = day;
    this.hour = hour;
    this.year_pillar = year_pillar;
    this.month_pillar = month_pillar;
    this.day_pillar = day_pillar;
    this.hour_pillar = hour_pillar;
    this.owner_feedback = owner_feedback;
    this.master_record = master_record;
    this.created_at = created_at ? new Date(created_at) : null;
    this.chart_id = chart_id;
  }

  /**
   * 方法1：通过后端 /api/calculate 计算得到 BZChart
   * @param {Object} params - { mingzhu_name?, gender, year, month, day, hour }
   * @returns {Promise<Object>} 返回普通对象，不是类实例
   */
  static async calculateFromAPI({ mingzhu_name = "", gender, year, month, day, hour }) {
    if (!gender || !year || !month || !day || hour == null) {
      throw new Error("性别、年、月、日、时为必填参数");
    }

    const apiBase = process.env.REACT_APP_API_BASE || "";
    const url = `${apiBase}/api/calculate`;

    try {
      // 发送请求时将年月日时转换为数字
      const response = await axios.post(url, {
        mingzhu_name: String(mingzhu_name || ""),
        gender: String(gender),
        year: String(year),
        month: String(month),
        day: String(day),
        hour: String(hour),
      });

      const data = response.data.data;
  
      // 直接返回普通对象，不创建类实例（避免序列化问题）
      return {
        user_id: data.user_id || null,
        mingzhu_name: String(data.mingzhu_name || ""),
        mingzhu_gender: String(data.mingzhu_gender || ""),
        shudi: String(data.shudi || ""),
        zhanti_type: String(data.zhanti_type || ""),
        zhanti: String(data.zhanti || ""),
        year: String(data.year || ""),
        month: String(data.month || ""),
        day: String(data.day || ""),
        hour: String(data.hour|| ""),
        year_pillar: String(data.year_pillar  || ""),
        month_pillar: String(data.month_pillar || ""),
        day_pillar: String(data.day_pillar || ""),
        hour_pillar: String(data.hour_pillar || ""),
        owner_feedback: String(data.owner_feedback || ""),
        master_record: String(data.master_record || ""),
        created_at: data.created_at || null,
        chart_id: data.chart_id || data.id || null,
      };
    } catch (error) {
      console.error("计算八字失败:", error);
      if (error.response) {
        throw new Error(error.response.data?.error || `服务器错误 ${error.response.status}`);
      }
      throw error;
    }
  }

  /**
   * 方法2：通过 chart_id 从后端数据库查询得到 BZChart
   * @param {number|string} chartId
   * @returns {Promise<Object>} 返回普通对象
   */
  static async fetchById(chartId) {
    if (!chartId) {
      throw new Error("chart_id 为必填参数");
    }

    const apiBase = process.env.REACT_APP_API_BASE || "";
    const url = `${apiBase}/api/charts/${encodeURIComponent(chartId)}`;
    const token = localStorage.getItem("access_token");

    try {
      const response = await axios.get(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const data = response.data;
   

      // 直接返回普通对象
      return {
        user_id: data.user_id || null,
        mingzhu_name: String(data.mingzhu_name || ""),
        mingzhu_gender: String(data.mingzhu_gender || ""),
        shudi: String(data.shudi || ""),
        zhanti_type: String(data.zhanti_type || ""),
        zhanti: String(data.zhanti || ""),
        year: String(data.year || ""),
        month: String(data.month || ""),
        day: String(data.day || ""),
        hour: String(data.hour || ""),
        year_pillar: String(data.year_pillar || ""),
        month_pillar: String(data.month_pillar || ""),
        day_pillar: String(data.day_pillar || ""),
        hour_pillar: String(data.hour_pillar || ""),
        owner_feedback: String(data.owner_feedback || ""),
        master_record: String(data.master_record || ""),
        created_at: data.created_at || null,
        chart_id: chartId,
       
        
      };
    } catch (error) {
      console.error("获取八字失败:", error);
      if (error.response?.status === 401) {
        throw new Error("未认证，请先登录");
      }
      if (error.response) {
        throw new Error(error.response.data?.error || `服务器错误 ${error.response.status}`);
      }
      throw error;
    }
  }

  /**
   * 方法4：静态方法，传入 BZChart 对象，返回包含藏干的完整对象 BZChartFull
   * @param {Object} bzChart - 普通对象
   * @returns {Object} BZChartFull
   */
  static getFullBZChart(bzChart) {
    if (!bzChart) return null;
   
    return {
      user_id: bzChart.user_id,
      mingzhu_name: bzChart.mingzhu_name,
      mingzhu_gender: bzChart.mingzhu_gender,
      year: bzChart.year,
      month: bzChart.month,
      day: bzChart.day,
      hour: bzChart.hour,
      year_pillar: bzChart.year_pillar,
      month_pillar: bzChart.month_pillar,
      day_pillar: bzChart.day_pillar,
      hour_pillar: bzChart.hour_pillar,
      owner_feedback: bzChart.owner_feedback,
      master_record: bzChart.master_record,
      created_at: bzChart.created_at,
      chart_id: bzChart.chart_id,
      // 地支藏干信息
      year_pillar_hidden_stems: getHiddenStems(bzChart.year_pillar),
      month_pillar_hidden_stems: getHiddenStems(bzChart.month_pillar),
      day_pillar_hidden_stems: getHiddenStems(bzChart.day_pillar),
      hour_pillar_hidden_stems: getHiddenStems(bzChart.hour_pillar),
  
    };
  }

  /**
   * 方法5：计算大运流年
   * @param {Object} bzChart - BZChart 对象
   * @returns {Promise<Object>} 返回大运流年数据
   */
  static async calculateDaYun(bzChart) {
    if (!bzChart) {
      throw new Error("bzChart 参数不能为空");
    }

    const { year, month, day, hour, mingzhu_gender } = bzChart;
    
    if (!year || !month || !day || hour == null || !mingzhu_gender) {
      throw new Error("缺少必要的出生信息：年、月、日、时、性别");
    }

    const apiBase = process.env.REACT_APP_API_BASE || "";
    const url = `${apiBase}/api/bazi/calculatedayun`;

    try {
      const response = await axios.post(url, {
        year: year,
        month: month,
        day: day,
        hour: hour,
        gender: String(mingzhu_gender),  
      });

      const data = response.data || {};

      // 解析返回的大运数据
      const direction =
        data.daYun?.direction || 
        data.daYun?.da_yun_direction || 
        data.meta?.daYun?.direction || 
        data.direction || 
        null;

      const qiyun_des = 
        data.daYun?.qiyun_des || 
        data.daYun?.qiyun || 
        data.meta?.daYun?.qiyun_des || 
        data.qiyun_des || 
        null;

      const previous_jieqi =
        data.daYun?.previous_jieqi || 
        data.meta?.daYun?.previous_jieqi || 
        null;

      const next_jieqi =
        data.daYun?.next_jieqi || 
        data.meta?.daYun?.next_jieqi || 
        null;

      const equivalent = 
        data.daYun?.qiyun_gap || 
        data.meta?.daYun?.qiyun_gap || 
        null;

      // 提取大运数组
      let daYunArr = data.da_yun || data.daYun || data.da_list || [];
      if (daYunArr && typeof daYunArr === "object" && !Array.isArray(daYunArr)) {
        if (daYunArr.da_yun && Array.isArray(daYunArr.da_yun)) {
          daYunArr = daYunArr.da_yun;
        } else {
          daYunArr = Object.values(daYunArr);
        }
      }
      if (!Array.isArray(daYunArr)) {
        daYunArr = [];
      }

      return {
        meta: {
          direction,
          qiyun_des,
          previous_jieqi,
          next_jieqi,
          equivalent,
        },
        daYunArr,
        currentPeriod: data.current_period || null,
        currentPeriodHiddenStems: data.current_period_hidden_stems || [],
        firstLiunianOfPeriod: data.first_liunian || null,
        firstLiunianHiddenStems: data.first_liunian_hidden_stems || [],
        liunianForCurrentYear: data.liunian_for_current_year || null,
        liunianCurrentYearHiddenStems: data.liunian_for_current_year_hidden_stems || [],
      };
    } catch (error) {
      console.error("计算大运失败:", error);
      if (error.response) {
        throw new Error(error.response.data?.error || `服务器错误 ${error.response.status}`);
      }
      throw error;
    }
  }
  
  /**
   * 方法6：通过阳历日期获取农历日期
   * @param {Object} params - { year, month, day, hour }
   * @returns {Promise<string>} 返回农历日期字符串
   */
  static async getLunarDate({ year, month, day, hour }) {
    if (!year || !month || !day || hour == null) {
      throw new Error("年、月、日、时为必填参数");
    }

    const apiBase = process.env.REACT_APP_API_BASE || "";
    const url = `${apiBase}/api/lunar_date`;

    try {
      const response = await axios.post(url, {
        year: parseInt(year, 10),
        month: parseInt(month, 10),
        day: parseInt(day, 10),
        hour: parseInt(hour, 10),
      });

      const data = response.data || {};
      
      // 返回农历日期字符串
      return String(data.lunar_date || data.lunar || "");
    } catch (error) {
      console.error("获取农历日期失败:", error);
      if (error.response) {
        throw new Error(error.response.data?.error || `服务器错误 ${error.response.status}`);
      }
      throw error;
    }
  }

  /**
   * 获取完整的BZChart对象（包含藏干等额外信息）
   * @param {Object} bzChart - 基础的bzChart对象
   * @returns {Object} 包含完整信息的对象
   */
  static getFullBZChart(bzChart) {
    if (!bzChart) return null;

    // 构造完整对象，确保所有必要字段都存在
    const fullChart = {
      ...bzChart,
      // 确保基本字段存在
      shudi: bzChart.shudi || "",
      zhanti_type: bzChart.zhanti_type || "",
      zhanti: bzChart.zhanti || "",
      mingzhu_name: bzChart.mingzhu_name || "",
      mingzhu_gender: bzChart.mingzhu_gender || "",
      year: bzChart.year || "",
      month: bzChart.month || "",
      day: bzChart.day || "",
      hour: bzChart.hour || "",
      year_pillar: bzChart.year_pillar || "",
      month_pillar: bzChart.month_pillar || "",
      day_pillar: bzChart.day_pillar || "",
      hour_pillar: bzChart.hour_pillar || "",
      owner_feedback: bzChart.owner_feedback || "",
      master_record: bzChart.master_record || "",
      chart_id: bzChart.chart_id || bzChart.id || null,
    };

    // 如果还没有隐藏干，添加它们
    if (!fullChart.year_pillar_hidden_stems) {
      fullChart.year_pillar_hidden_stems = getHiddenStems(fullChart.year_pillar);
      fullChart.month_pillar_hidden_stems = getHiddenStems(fullChart.month_pillar);
      fullChart.day_pillar_hidden_stems = getHiddenStems(fullChart.day_pillar);
      fullChart.hour_pillar_hidden_stems = getHiddenStems(fullChart.hour_pillar);
    }

    return fullChart;
  }

  /**
   * 转换为普通对象（用于传递给其他页面）
   * @returns {Object}
   */
  toPayload() {
    return {
      user_id: this.user_id,
      mingzhu_name: this.mingzhu_name,
      mingzhu_gender: this.mingzhu_gender,
      shudi: this.shudi,
      zhanti_type: this.zhanti_type,
      zhanti: this.zhanti,
      year: this.year,
      month: this.month,
      day: this.day,
      hour: this.hour,
      year_pillar: this.year_pillar,
      month_pillar: this.month_pillar,
      day_pillar: this.day_pillar,
      hour_pillar: this.hour_pillar,
      owner_feedback: this.owner_feedback,
      master_record: this.master_record,
      created_at: this.created_at?.toISOString() || null,
      chart_id: this.id,
      //chart_id: this.chart_id,
    };
  }
}

export default BZChart;